import {
  login,
  logout,
  signUp,
  resendOtp,
  verifyOtp,
  editProfile,
  deleteAccount,
  resetPassword,
  changePassword,
  forgotPassword,
  verifyPatientOTP,
  verifyPatientIdentityPassword,
  getVisitTreatmentImages,
} from "./userApi";
import { createSlice } from "@reduxjs/toolkit";
import { DOCTOR_ROLE } from "../../../utils/constants";
import { CacheManager } from "../../../utils";
import {
  writeLastActivity,
  clearLastActivity,
} from "../../../utils/lastActivity";

export const userSlice = createSlice({
  name: "user",
  initialState: {
    user: null,
    error: null,
    email: null,
    loading: "idle",
    patientToken: null,
    userPractices: null,
  },
  reducers: {
    customLogout: (state) => {
        state.user = null;
        state.patientToken = null;
        CacheManager.clear();
        clearLastActivity();
    },
    clearCache: () => {
        // Clear all caches
        CacheManager.clear();
    },
    setPractice: (state, action) => {
      state.user = { ...state.user, defaultPractice: action.payload };
    },
    // A 403 from the impact API is the server's live answer; clear the stale
    // login-time flag so the page and its sidebar entry go without a re-login.
    revokeImpactAccess: (state, action) => {
      const practiceId = String(action.payload ?? "");
      if (!practiceId) return;
      const isRevoked = (practice) => String(practice?.id ?? "") === practiceId;
      if (isRevoked(state.user?.defaultPractice)) {
        state.user.defaultPractice = {
          ...state.user.defaultPractice,
          attributedMetricsEnabled: false,
        };
      }
      if (Array.isArray(state.userPractices)) {
        state.userPractices = state.userPractices.map((practice) =>
          isRevoked(practice)
            ? { ...practice, attributedMetricsEnabled: false }
            : practice
        );
      }
    },
    logoutPatient: (state) => {
      state.patientToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUp.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(signUp.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload.error;
      })
      .addCase(logout.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = "succeeded";
        state.user = null;
        state.unreadCount = 0;
        state.userNotifications = [];
        clearLastActivity();
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload.error;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload.error;
      })
      .addCase(login.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.user = action.payload.user;
        writeLastActivity(Date.now());
        if (action?.payload?.user?.role === DOCTOR_ROLE) {
          state.userPractices = action?.payload?.doctorPractice;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload.error;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.email = action.payload;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload.error;
      })
      .addCase(resetPassword.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = "succeeded";
        state.email = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload.error;
      })
      .addCase(editProfile.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(editProfile.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.user = action.payload;
      })
      .addCase(editProfile.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload.error;
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = "succeeded";
      })
      .addCase(changePassword.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(resendOtp.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(resendOtp.fulfilled, (state) => {
        state.loading = "succeeded";
      })
      .addCase(resendOtp.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(deleteAccount.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.user = null;
        state.loading = "succeeded";
      })
      .addCase(deleteAccount.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(getVisitTreatmentImages.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getVisitTreatmentImages.fulfilled, (state) => {
        state.loading = "succeeded";
      })
      .addCase(getVisitTreatmentImages.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(verifyPatientIdentityPassword.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(verifyPatientIdentityPassword.fulfilled, (state) => {
        state.loading = "succeeded";
      })
      .addCase(verifyPatientIdentityPassword.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(verifyPatientOTP.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(verifyPatientOTP.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.patientToken = action.payload
      })
      .addCase(verifyPatientOTP.rejected, (state) => {
        state.loading = "failed";
      });
  },
});

export const { customLogout, setPractice, logoutPatient, clearCache, revokeImpactAccess } = userSlice.actions;

export default userSlice.reducer;